import { getCookie, setCookie } from "../utils/cookieUtils";
import { UAParser } from "ua-parser-js";
const uap = new UAParser();
const browser_data = uap.getResult();
export class EventManager {
    constructor(eventData, includeConsentStatus = false) {
        this.name = eventData.name;
        this.sessionId = getCookie("SessionId");
        this.source = window.location.href;
        this.eventProperties = eventData.eventProperties || {};
        this.date = new Date();
        this.baseUrl = "https://www.inito.com/";
        const shouldCreateSession = getCookie("SessionStart") == "" ||
            this.date - new Date(getCookie("SessionStart")) >
                30 * 60 * 1000;
        // Prevent duplicate session creation with a flag
        const sessionCreationInProgress = getCookie("SessionCreating");
        if (shouldCreateSession && !sessionCreationInProgress) {
            setCookie("SessionCreating", "true", 30 / 86400); // 30 seconds in days
            this.createOrUpdateSession("create", "session_start")
                .then(() => setCookie("SessionCreating", "", -1)) // Clear flag on success
                .catch(() => setCookie("SessionCreating", "", -1)); // Clear flag on error
        }
        if (includeConsentStatus) {
            this.eventProperties.consent = this.getConsentStatusFromCookies();
        }
        const params = new URLSearchParams(window.location.search);
        const workflow = params.get("workflow");
        const device_type = (browser_data.os.name?.toLowerCase() === "android" &&
            workflow !== "og") ||
            workflow === "ng"
            ? "IWR"
            : "IFM";
        this.consentStatus = this.getConsentStatusFromCookies();
        this.eventProperties.url = window.location.href;
        this.eventProperties.referrer = document.referrer;
        this.eventProperties.user_agent =
            browser_data.device.type || this.getDeviceType();
        this.eventProperties.browser_name = browser_data.browser.name;
        this.eventProperties.languages = navigator.languages;
        this.eventProperties.os_name = browser_data.os.name;
        this.eventProperties.os_version = browser_data.os.version;
        this.eventProperties.mobile_brand_name = browser_data.device.vendor;
        this.eventProperties.mobile_model_name = browser_data.device.model;
        this.eventProperties.model_name_screen = this.getDeviceTypeByScreenSize();
        this.eventProperties.engagement_time =
            this.date.getTime() - new Date(getCookie("SessionStart")).getTime();
        this.eventProperties.device_type = device_type;
    }
    getConsentStatusFromCookies() {
        return {
            performance_cookie_consent: getCookie("performance_cookie_consent") === "allow" ? 1 : 0,
            functional_cookie_consent: getCookie("functional_cookie_consent") === "allow" ? 1 : 0,
            advertising_cookie_consent: getCookie("advertising_cookie_consent") === "allow" ? 1 : 0,
        };
    }
    async apiCall(url, method, body) {
        try {
            const response = await fetch(`${this.baseUrl}${url}`, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Basic ${process.env.NEXT_PUBLIC_WEB_AUTH_TOKEN}`,
                },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                throw new Error(`API call failed: ${response.statusText}`);
            }
            return response;
        }
        catch (error) {
            console.error("Error in API call:", error);
            throw error;
        }
    }
    async createOrUpdateSession(action, event, interactionType) {
        const sessionData = {
            session_id: this.sessionId,
            consent: this.consentStatus,
            create_time: this.date,
            source: this.source,
            interaction_type: interactionType,
        };
        // Add event only for create action
        if (action === "create" && event) {
            sessionData.event = event;
        }
        const url = action === "create" ? "/session_with_consent" : "/sessions/update_by_id";
        const method = action === "create" ? "POST" : "PUT";
        const res = await this.apiCall(url, method, sessionData);
        const data = await res.json();
        if (data.restriction_required)
            setCookie("RestrictionRequired", "true");
        return data;
    }
    async sendEvent() {
        if (this.name.includes("_page_loaded")) {
            if (document.readyState !== "complete") {
                await new Promise((resolve) => {
                    window.addEventListener("load", () => {
                        setTimeout(() => {
                            this.eventProperties.page_load_time =
                                performance.timing.domContentLoadedEventEnd -
                                    performance.timing.navigationStart;
                            resolve(undefined);
                        }, 0);
                    });
                });
            }
            else {
                this.eventProperties.page_load_time =
                    performance.timing.domContentLoadedEventEnd -
                        performance.timing.navigationStart;
            }
        }
        document.cookie = `SessionStart=${this.date.toISOString()}; path=/; secure; SameSite=Strict`;
        const eventData = {
            event: this.name,
            event_properties: this.eventProperties,
            session_id: this.sessionId,
            event_created_at: this.date,
            new_session: this.newSession || false,
        };
        await this.apiCall("/events", "POST", eventData);
        const ctx = window.__inito__ || {};
        if (!ctx.high_intent_fired && /(scrolled|clicked)/i.test(this.name)) {
            ctx.high_intent_fired = true;
            await this.apiCall("/api/intent-action", "POST", { session_id: this.sessionId });
        }
    }
    getDeviceType() {
        const userAgent = navigator.userAgent.toLowerCase();
        const isMobile = /android|iphone|ipod|blackberry|iemobile|opera mini/.test(userAgent);
        const isTablet = /ipad|tablet|playbook|silk/.test(userAgent);
        if (isTablet)
            return "Tablet";
        if (isMobile)
            return "Mobile";
        return "Laptop/Desktop";
    }
    getDeviceTypeByScreenSize() {
        const width = window.innerWidth;
        if (width <= 768) {
            return "Mobile";
        }
        else if (width > 768 && width <= 1024) {
            return "Tablet";
        }
        else {
            return "Desktop";
        }
    }
}
// Export for global usage
if (typeof window !== "undefined") {
    window.__inito__ = window.__inito__ || {};
    window.__inito__.EventManager = EventManager;
    // Initialize flag to ensure first high-intent action is tracked only once per page load
    if (window.__inito__.high_intent_fired === undefined) {
        window.__inito__.high_intent_fired = false;
    }
}
